import { sha256 } from '@noble/hashes/sha2'
import { bytesToHex, hexToBytes } from '@noble/hashes/utils'
import type { FlatSandbox, Sandbox, SandboxFn } from './types.js'

/**
 * Validates that a date falls within a time range
 */
export const checkTimeRange = (date: string | Date, start: Date, end: Date): void => {
  const d = new Date(date)

  if (d.getTime() < start.getTime() || d.getTime() > end.getTime()) {
    throw new Error(`date ${date} is out of range [${start} - ${end}]`)
  }
}

/**
 * Gets a time range starting from now
 */
export const getTimeRangeFromNow = (maxAgeInSeconds: number): [Date, Date] => {
  const now = new Date()
  const end = new Date(now.getTime() + maxAgeInSeconds * 1000)

  return [now, end]
}

export const sleep = (milliseconds: number): Promise<void> => {
  return new Promise(resolve => {
    setTimeout(resolve, milliseconds)
  })
}

/**
 * Flattens a nested sandbox object into a flat key-value map
 *
 * Example:
 *   { user: { get: fn1, update: fn2 } }
 *   => { 'user.get': fn1, 'user.update': fn2 }
 */
export const flattenSandbox = (
  sandbox: Sandbox = {},
  { connector = '.' }: { connector?: string } = {},
): FlatSandbox => {
  const result: FlatSandbox = {}

  const flatten = (path: string, value: unknown): void => {
    if (typeof value === 'function') {
      result[path] = value as SandboxFn
      return
    }

    if (value && typeof value === 'object') {
      for (const key in value as Record<string, unknown>) {
        const nextPath = path === '' ? key : path + connector + key
        flatten(nextPath, (value as Record<string, unknown>)[key])
      }
    }
  }

  flatten('', sandbox)

  return result
}

/**
 * Converts a number or hex string to a 32-byte Uint8Array
 */
export const to32Bytes = (input: number | string | Uint8Array): Uint8Array => {
  if (input instanceof Uint8Array) {
    if (input.length !== 32) {
      throw new Error(`expected 32 bytes, got ${input.length}`)
    }
    return input
  }

  let hex = typeof input === 'string' ? input : input.toString(16)
  hex = hex.padStart(64, '0')

  return hexToBytes(hex)
}

/**
 * Wraps all methods in a sandbox with a wrapper function
 */
export const wrapSandbox = (
  sandbox: FlatSandbox,
  wrapper: (name: string, fn: SandboxFn) => SandboxFn,
): FlatSandbox => {
  const result: FlatSandbox = {}

  for (const name in sandbox) {
    result[name] = wrapper(name, sandbox[name])
  }

  return result
}

/**
 * Applies multiple wrappers to a sandbox
 *
 * Wrappers are applied right-to-left (last wrapper is innermost).
 */
export const useSandboxWrappers = (
  sandbox: FlatSandbox,
  wrappers: Array<(name: string, fn: SandboxFn) => SandboxFn>,
): FlatSandbox => {
  return wrapSandbox(sandbox, (name, fn) => {
    let wrapped = fn

    for (let i = wrappers.length - 1; i >= 0; i--) {
      wrapped = wrappers[i](name, wrapped)
    }

    return wrapped
  })
}

export const log = (...args: unknown[]): void => {
  console.log(new Date().toISOString(), ...args)
}

export const textEncoder = new TextEncoder()
export const textDecoder = new TextDecoder('utf-8')

export { hexToBytes, bytesToHex, sha256 }
